/**
 * OrchestratorLogger - v2 Taxonomy Wrapper for DocumentOrchestrator logging
 *
 * Domain: orchestrator
 * Taxonomy: orchestrator.{operation}[.{flavor}]
 *
 * Operations:
 * - init: Service initialization and document metadata loading
 * - render: Render cycle and editor selection
 * - font: Font warmup, resolution, and restoration
 * - pageSetup: Page setup initialization
 * - save: Document save operations
 *
 * Flavors: .start, .complete, .fail, .perf, .diag, .skip
 *
 * All methods capture full stack traces via captureCallerSource().
 *
 * @example
 * OrchestratorLogger.init.start('abc', 'v1', 'EdgeDocsEditorV3');
 * OrchestratorLogger.render.start({ editorType: 'EdgeDocsEditorV3' });
 * OrchestratorLogger.font.warmup.complete(150);
 */

import { WeBelieveLogger, captureCallerSource } from '../WeBelieveLogger';

export const OrchestratorLogger = {
  // ============================================================================
  // INIT OPERATIONS - orchestrator.init.*
  // ============================================================================
  init: {
    /**
     * Log service initialization start
     * Component: orchestrator.init.start
     */
    start: (docId: string, versionId: string | null, preferredEditor: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'orchestrator.init.start', 'Initializing services with IDs', source, {
        docId,
        versionId: versionId || 'active (default)',
        preferredEditor,
        ...data
      });
    },

    /**
     * Log DocumentService created
     * Component: orchestrator.init.service.created
     */
    service: {
      created: (instanceId: string, hasGetCachedDocument: boolean, hasGetDocumentMeta: boolean, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.init.service.created', 'Created NEW DocumentService instance', source, {
          instanceId,
          hasGetCachedDocument,
          hasGetDocumentMeta,
          ...data
        });
      },
    },

    /**
     * Log meta operations
     */
    meta: {
      /**
       * Log meta effect triggered
       * Component: orchestrator.init.meta.trigger
       */
      trigger: (docId: string, hasServices: boolean, hasDocument: boolean, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.init.meta.trigger', 'useEffect triggered for getDocumentMeta', source, {
          docId,
          hasServices,
          hasDocument,
          ...data
        });
      },

      /**
       * Log meta result
       * Component: orchestrator.init.meta.result
       */
      result: (meta: Record<string, unknown> | null, hasTitle: boolean, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.init.meta.result', 'Meta result', source, {
          meta,
          hasTitle,
          ...data
        });
      },

      /**
       * Log background meta fetch complete
       * Component: orchestrator.init.meta.complete
       */
      complete: (backendTitle: string | undefined, preservedTitle: string, usingInitialTitle: boolean, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.init.meta.complete', 'Background meta fetch complete', source, {
          backendTitle,
          preservedTitle,
          usingInitialTitle,
          ...data
        });
      },
    },

    /**
     * Log title operations
     */
    title: {
      /**
       * Log using initialTitle from URL
       * Component: orchestrator.init.title.fromUrl
       */
      fromUrl: (initialTitle: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.init.title.fromUrl', 'Using initialTitle from URL', source, {
          initialTitle,
          ...data
        });
      },
    },

    /**
     * Log cache operations
     */
    cache: {
      /**
       * Log cache check
       * Component: orchestrator.init.cache.check
       */
      check: (docId: string, hasCached: boolean, cachedTitle?: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.init.cache.check', 'Checking cache for docId', source, {
          docId,
          hasCached,
          cachedTitle,
          ...data
        });
      },
    },
  },

  // ============================================================================
  // RENDER OPERATIONS - orchestrator.render.*
  // ============================================================================
  render: {
    /**
     * Log render check
     * Component: orchestrator.render.check
     */
    check: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.render.check', 'render check', source, data);
    },

    /**
     * Log rendering start
     * Component: orchestrator.render.start
     */
    start: (editorType: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.render.start', `RENDERING: ${editorType}`, source, {
        editorType,
        ...data
      });
    },

    /**
     * Log editor selection
     * Component: orchestrator.render.editorSelect
     */
    editorSelect: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.render.editorSelect', 'editor selection', source, data);
    },

    /** @deprecated Use OrchestratorLogger.render.start() */
    rendering: (editorType: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.render.start', `RENDERING: ${editorType}`, source, {
        editorType,
        ...data
      });
    },

    /** @deprecated Use OrchestratorLogger.render.editorSelect() */
    editorSelection: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.render.editorSelect', 'editor selection', source, data);
    },
  },

  // ============================================================================
  // FONT OPERATIONS - orchestrator.font.*
  // ============================================================================
  font: {
    /**
     * Log font warmup operations
     */
    warmup: {
      /**
       * Log font warmup start
       * Component: orchestrator.font.warmup.start
       */
      start: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('info', 'orchestrator.font.warmup.start', 'Starting font warmup', source, data);
      },

      /**
       * Log font warmup progress
       * Component: orchestrator.font.warmup.progress
       */
      progress: (progress: number, phase: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.font.warmup.progress', `Font warmup progress: ${progress}% (${phase})`, source, {
          progress,
          phase,
          ...data
        });
      },

      /**
       * Log font warmup complete
       * Component: orchestrator.font.warmup.complete
       */
      complete: (durationMs: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.performanceWithSource('orchestrator.font.warmup.complete', 'Font warmup complete', durationMs, source, data);
      },
    },

    /**
     * Log system preferences operations
     */
    preferences: {
      /**
       * Log loading system preferences
       * Component: orchestrator.font.preferences.load.start
       */
      loadStart: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.font.preferences.load.start', 'Loading system preferences for font initialization', source, data);
      },

      /**
       * Log system preferences loaded
       * Component: orchestrator.font.preferences.load.complete
       */
      loadComplete: (preferences: Record<string, unknown>, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.font.preferences.load.complete', 'System preferences loaded', source, {
          preferences,
          ...data
        });
      },

      /**
       * Log using system preference font
       * Component: orchestrator.font.preferences.resolved
       */
      resolved: (prefFont: string, mappedFont: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.font.preferences.resolved', `Using system preference font: ${prefFont} → ${mappedFont}`, source, {
          prefFont,
          mappedFont,
          ...data
        });
      },

      /**
       * Log unknown system preference font
       * Component: orchestrator.font.preferences.gap.unknown
       */
      unknown: (prefFont: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('warn', 'orchestrator.font.preferences.gap.unknown', `Unknown system preference font: ${prefFont}, using fallback`, source, {
          prefFont,
          ...data
        });
      },
    },

    /**
     * Log fallback operations
     */
    fallback: {
      /**
       * Log using fallback font
       * Component: orchestrator.font.fallback.using
       */
      using: (fontName: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.font.fallback.using', `Using fallback font: ${fontName}`, source, {
          fontName,
          ...data
        });
      },

      /**
       * Log FontResolver fallback set
       * Component: orchestrator.font.fallback.set
       */
      set: (fontUid: string, documentFont: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.font.fallback.set', `Setting FontResolver fallback to: ${fontUid} (from system preference: ${documentFont})`, source, {
          fontUid,
          documentFont,
          ...data
        });
      },
    },

    /**
     * Log font restoration operations
     */
    restore: {
      /**
       * Log font restored from document
       * Component: orchestrator.font.restore.complete
       */
      complete: (fontFamily: string, fontWeight?: number, fontStyle?: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.font.restore.complete', 'Font restored from document', source, {
          fontFamily,
          fontWeight,
          fontStyle,
          ...data
        });
      },

      /**
       * Log font restoration skipped
       * Component: orchestrator.font.restore.skip
       */
      skip: (reason: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.font.restore.skip', `Skipping font restoration - ${reason}`, source, data);
      },
    },

    /**
     * Log font family update
     * Component: orchestrator.font.update
     */
    update: (fontFamily: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.font.update', `Updating font family from system preferences: ${fontFamily}`, source, {
        fontFamily,
        ...data
      });
    },

    /**
     * Log default font size initialization
     * Component: orchestrator.font.defaultSize
     */
    defaultSize: (fontSize: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.font.defaultSize', `Initializing with default font size from System Preferences: ${fontSize}`, source, {
        fontSize,
        ...data
      });
    },
  },

  // ============================================================================
  // SAVE OPERATIONS - orchestrator.save.*
  // ============================================================================
  save: {
    /**
     * Log save started
     * Component: orchestrator.save.start
     */
    start: (docId: string, versionId: string | null, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'orchestrator.save.start', 'Saving document', source, {
        docId,
        versionId: versionId || 'active',
        timestamp: new Date().toISOString(),
        ...data
      });
    },

    /**
     * Log document state captured
     * Component: orchestrator.save.state.captured
     */
    state: {
      captured: (textLength: number, textPreview: string, hasParagraphs: boolean, paragraphCount?: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.save.state.captured', 'Document state captured', source, {
          textLength,
          textPreview: textPreview.substring(0, 100),
          hasParagraphs,
          paragraphCount,
          ...data
        });
      },

      /**
       * Log captured state with full details
       * Component: orchestrator.save.state.full
       */
      full: (textLength: number, paragraphCount: number, hasPageSetup: boolean, hasFontSettings: boolean, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.save.state.full', 'Captured state', source, {
          textLength,
          paragraphCount,
          hasPageSetup,
          hasFontSettings,
          ...data
        });
      },
    },

    /**
     * Log AST payload
     * Component: orchestrator.save.ast
     */
    ast: (blockCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.save.ast', 'Saving AST payload', source, {
        blockCount,
        ...data
      });
    },

    /**
     * Log save complete
     * Component: orchestrator.save.complete
     */
    complete: (docId: string, durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('orchestrator.save.complete', 'Document saved', durationMs, source, {
        docId,
        ...data
      });
    },

    /**
     * Log save failed
     * Component: orchestrator.save.fail
     */
    fail: (docId: string, error: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'orchestrator.save.fail', `Document save failed: ${error}`, source, {
        docId,
        error,
        ...data
      });
    },

    /**
     * Log empty document warning
     * Component: orchestrator.save.gap.empty
     */
    emptyWarning: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'orchestrator.save.gap.empty', 'Warning: Document text is empty. Saving empty document.', source, data);
    },

    /**
     * Log document state with spans
     * Component: orchestrator.save.state.withSpans
     */
    stateWithSpans: (blockCount: number, spanCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.save.state.withSpans', 'Got document state WITH spans', source, {
        blockCount,
        spanCount,
        ...data
      });
    },

    /**
     * Log fallback to sync method
     * Component: orchestrator.save.gap.fallbackSync
     */
    fallbackSync: (error: unknown, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'orchestrator.save.gap.fallbackSync', 'getDocumentStateWithSpans failed, falling back to sync', source, {
        error: String(error),
        ...data
      });
    },

    /**
     * Log editor state not available
     * Component: orchestrator.save.fail.noState
     */
    noState: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'orchestrator.save.fail.noState', 'Cannot save: editor state not available', source, data);
    },
  },

  // ============================================================================
  // PAGE SETUP OPERATIONS - orchestrator.pageSetup.*
  // ============================================================================
  pageSetup: {
    /**
     * Log page setup initialization from document metadata
     * Component: orchestrator.pageSetup.init
     */
    init: (pageSetup: Record<string, unknown>, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'orchestrator.pageSetup.init', 'Initializing page setup from document metadata', source, {
        pageSetup,
        ...data
      });
    },

    /**
     * Log page setup applied from dialog
     * Component: orchestrator.pageSetup.apply
     */
    apply: (config: Record<string, unknown>, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'orchestrator.pageSetup.apply', 'Page Setup Applied from Dialog', source, {
        config,
        ...data
      });
    },

    /**
     * Log WASM format conversion
     * Component: orchestrator.pageSetup.wasmFormat
     */
    wasmFormat: (wasmFormat: Record<string, unknown>, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.pageSetup.wasmFormat', 'WASM Format (sent to typesetter)', source, {
        wasmFormat,
        ...data
      });
    },

    /**
     * Log conversion details
     * Component: orchestrator.pageSetup.conversion
     */
    conversion: (details: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.pageSetup.conversion', 'Conversion Details', source, details);
    },

    /**
     * Auto-save operations
     */
    autoSave: {
      /**
       * Log auto-save start
       * Component: orchestrator.pageSetup.autoSave.start
       */
      start: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.pageSetup.autoSave.start', 'Auto-saving page setup changes to database', source, data);
      },

      /**
       * Log auto-save no state warning
       * Component: orchestrator.pageSetup.autoSave.gap.noState
       */
      noState: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('warn', 'orchestrator.pageSetup.autoSave.gap.noState', 'Cannot auto-save page setup: editor state not available', source, data);
      },

      /**
       * Log auto-save payload
       * Component: orchestrator.pageSetup.autoSave.payload
       */
      payload: (docId: string, blockCount: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.pageSetup.autoSave.payload', 'Auto-saving page setup to database', source, {
          docId,
          blockCount,
          ...data
        });
      },

      /**
       * Log auto-save complete
       * Component: orchestrator.pageSetup.autoSave.complete
       */
      complete: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('info', 'orchestrator.pageSetup.autoSave.complete', 'Page setup saved successfully to database', source, data);
      },

      /**
       * Log auto-save fail
       * Component: orchestrator.pageSetup.autoSave.fail
       */
      fail: (error: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('error', 'orchestrator.pageSetup.autoSave.fail', `Auto-save of page setup failed: ${error}`, source, { error, ...data });
      },
    },
  },

  // ============================================================================
  // ZOOM/RENDER OPERATIONS - orchestrator.zoom.*
  // ============================================================================
  zoom: {
    /**
     * Log fit-width calculation
     * Component: orchestrator.zoom.fitWidth
     */
    fitWidth: (viewportWidth: number, pageWidth: number, zoomPercent: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.zoom.fitWidth', `Fit Width - Viewport: ${viewportWidth}px, Page: ${pageWidth}px, Zoom: ${zoomPercent}%`, source, {
        viewportWidth,
        pageWidth,
        zoomPercent,
        ...data
      });
    },
  },

  // ============================================================================
  // FONT CHANGE OPERATIONS - orchestrator.fontChange.*
  // ============================================================================
  fontChange: {
    /**
     * Log font family change request
     * Component: orchestrator.fontChange.family.start
     */
    familyStart: (family: string, fontUid: string, fontWeight: number, fontStyle: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.fontChange.family.start', 'onChangeFontFamily called', source, {
        family,
        fontUid,
        fontWeight,
        fontStyle,
        ...data
      });
    },

    /**
     * Log dynamic font loading
     * Component: orchestrator.fontChange.load.start
     */
    loadStart: (fontUid: string, family: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.fontChange.load.start', `Loading font dynamically: ${fontUid} (${family})`, source, {
        fontUid,
        family,
        ...data
      });
    },

    /**
     * Log font load success
     * Component: orchestrator.fontChange.load.complete
     */
    loadComplete: (fontUid: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.fontChange.load.complete', `Font loaded successfully: ${fontUid}`, source, {
        fontUid,
        ...data
      });
    },

    /**
     * Log font load failure
     * Component: orchestrator.fontChange.load.fail
     */
    loadFail: (fontUid: string, error?: unknown, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'orchestrator.fontChange.load.fail', `Failed to load font: ${fontUid}`, source, {
        fontUid,
        error: error ? String(error) : undefined,
        ...data
      });
    },

    /**
     * Log font change on selection
     * Component: orchestrator.fontChange.selection
     */
    selection: (family: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.fontChange.selection', 'Font change on selected text - TODO: Apply to selection only', source, {
        family,
        ...data
      });
    },

    /**
     * Log pending font format set
     * Component: orchestrator.fontChange.pending
     */
    pending: (family: string, fontWeight: number, fontStyle: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.fontChange.pending', 'Font change with no selection - setting pending format', source, {
        family,
        fontWeight,
        fontStyle,
        ...data
      });
    },

    /**
     * Log font size change on selection
     * Component: orchestrator.fontChange.sizeSelection
     */
    sizeSelection: (sizePx: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.fontChange.sizeSelection', 'Font size change on selected text - TODO: Apply to selection only', source, {
        sizePx,
        ...data
      });
    },

    /**
     * Log pending font size set
     * Component: orchestrator.fontChange.sizePending
     */
    sizePending: (sizePx: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.fontChange.sizePending', 'Font size change with no selection - setting pending format', source, {
        sizePx,
        ...data
      });
    },

    /**
     * Log font size applied to selection via formatting spans
     * Component: orchestrator.fontChange.sizeApplied
     */
    sizeApplied: (sizePx: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'orchestrator.fontChange.sizeApplied', 'Font size applied to selection via formatting spans', source, {
        sizePx,
        ...data
      });
    },
  },

  // ============================================================================
  // LINE SPACING OPERATIONS - orchestrator.lineSpacing.*
  // ============================================================================
  lineSpacing: {
    /**
     * Log no paragraphs warning
     * Component: orchestrator.lineSpacing.gap.noParagraphs
     */
    noParagraphs: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'orchestrator.lineSpacing.gap.noParagraphs', 'No paragraphs available for line spacing change', source, data);
    },

    /**
     * Log per-paragraph line spacing change
     * Component: orchestrator.lineSpacing.change
     */
    change: (paragraphCount: number, multiplier: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.lineSpacing.change', 'Per-paragraph line spacing change', source, {
        paragraphCount,
        multiplier,
        ...data
      });
    },

    /**
     * Log updateParagraphs call
     * Component: orchestrator.lineSpacing.update.start
     */
    updateStart: (paragraphIds: string[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.lineSpacing.update.start', 'Calling updateParagraphs', source, {
        paragraphIds,
        ...data
      });
    },

    /**
     * Log updateParagraphs success
     * Component: orchestrator.lineSpacing.update.complete
     */
    updateComplete: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.lineSpacing.update.complete', 'updateParagraphs called successfully', source, data);
    },

    /**
     * Log updateParagraphs not available
     * Component: orchestrator.lineSpacing.update.fail
     */
    updateFail: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'orchestrator.lineSpacing.update.fail', 'editorRef.current?.updateParagraphs is not available!', source, data);
    },
  },

  // ============================================================================
  // TOOLBAR OPERATIONS - orchestrator.toolbar.*
  // Selection capture and preview callbacks for MS Word-like toolbar behavior
  // ============================================================================
  toolbar: {
    /**
     * Log selection captured before toolbar interaction
     * Component: orchestrator.toolbar.selection.captured
     */
    selectionCaptured: (event: string, anchorOffset: number | undefined, focusOffset: number | undefined, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.toolbar.selection.captured', `Selection captured on ${event}`, source, {
        event,
        anchorOffset,
        focusOffset,
        ...data
      });
    },

    /**
     * Log selection getter called (returning stored vs current)
     * Component: orchestrator.toolbar.selection.get
     */
    selectionGet: (usedStored: boolean, hasStored: boolean, hasCurrent: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.toolbar.selection.get', `getSelection() called - usedStored: ${usedStored}`, source, {
        usedStored,
        hasStored,
        hasCurrent,
        ...data
      });
    },

    /**
     * Log preview callback invoked
     * Component: orchestrator.toolbar.preview.start
     */
    previewStart: (property: string, value: unknown, rangeCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'orchestrator.toolbar.preview.start', `Preview ${property} = ${value} for ${rangeCount} ranges`, source, {
        property,
        value,
        rangeCount,
        ...data
      });
    },

    /**
     * Log commit callback invoked
     * Component: orchestrator.toolbar.commit
     */
    commit: (property: string, value: unknown, rangeCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'orchestrator.toolbar.commit', `Commit ${property} = ${value} for ${rangeCount} ranges`, source, {
        property,
        value,
        rangeCount,
        ...data
      });
    },

    /**
     * Log cancel callback invoked
     * Component: orchestrator.toolbar.cancel
     */
    cancel: (property: string, originalValue: unknown, rangeCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.toolbar.cancel', `Cancel ${property}, restore to ${originalValue} for ${rangeCount} ranges`, source, {
        property,
        originalValue,
        rangeCount,
        ...data
      });
    },

    /**
     * Log dropdown opened with selection state
     * Component: orchestrator.toolbar.dropdown.open
     */
    dropdownOpen: (property: string, currentValue: unknown, rangeCount: number, hasSelection: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.toolbar.dropdown.open', `Dropdown ${property} opened`, source, {
        property,
        currentValue,
        rangeCount,
        hasSelection,
        ...data
      });
    },

    /**
     * Log context callbacks registered
     * Component: orchestrator.toolbar.context.registered
     */
    contextRegistered: (hasGetSelection: boolean, hasGetDocumentState: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'orchestrator.toolbar.context.registered', 'ToolbarContext callbacks registered', source, {
        hasGetSelection,
        hasGetDocumentState,
        ...data
      });
    },

    /**
     * Log selection bound for toolbar operation
     * Component: orchestrator.toolbar.selection.bound
     */
    selectionBound: (anchorOffset: number | undefined, focusOffset: number | undefined, isCollapsed: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.toolbar.selection.bound', `Selection bound [${anchorOffset} → ${focusOffset}]`, source, {
        anchorOffset,
        focusOffset,
        isCollapsed,
        ...data
      });
    },

    /**
     * Log paragraph ranges calculated
     * Component: orchestrator.toolbar.ranges.calculated
     */
    rangesCalculated: (rangeCount: number, selStart: number, selEnd: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.toolbar.ranges.calculated', `Calculated ${rangeCount} paragraph ranges`, source, {
        rangeCount,
        selStart,
        selEnd,
        ...data
      });
    },

    /**
     * Log no ranges available (selection collapsed or missing)
     * Component: orchestrator.toolbar.ranges.none
     */
    rangesNone: (reason: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'orchestrator.toolbar.ranges.none', `No ranges available: ${reason}`, source, {
        reason,
        ...data
      });
    },
  },

  // ============================================================================
  // FORMATTING OPERATIONS - orchestrator.formatting.*
  // ============================================================================
  formatting: {
    /**
     * Bold formatting operations
     */
    bold: {
      /**
       * Log no selection warning
       * Component: orchestrator.formatting.bold.gap.noSelection
       */
      noSelection: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('warn', 'orchestrator.formatting.bold.gap.noSelection', 'Cannot toggle bold: no selection or document state', source, data);
      },

      /**
       * Log loading bold font
       * Component: orchestrator.formatting.bold.loadFont.start
       */
      loadFontStart: (fontUid: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.bold.loadFont.start', `Loading bold font before setPendingFormat: ${fontUid}`, source, { fontUid, ...data });
      },

      /**
       * Log bold font loaded
       * Component: orchestrator.formatting.bold.loadFont.complete
       */
      loadFontComplete: (fontUid: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.bold.loadFont.complete', `Bold font loaded: ${fontUid}`, source, { fontUid, ...data });
      },

      /**
       * Log bold font load failed
       * Component: orchestrator.formatting.bold.loadFont.fail
       */
      loadFontFail: (fontUid: string, error?: unknown, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('error', 'orchestrator.formatting.bold.loadFont.fail', `Failed to load bold font: ${fontUid}`, source, { fontUid, error: error ? String(error) : undefined, ...data });
      },

      /**
       * Log setting pending bold format
       * Component: orchestrator.formatting.bold.pending
       */
      pending: (fontUid: string, fontWeight: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.bold.pending', 'Setting pending bold format (collapsed cursor)', source, { fontUid, fontWeight, ...data });
      },

      /**
       * Log bold toggle start
       * Component: orchestrator.formatting.bold.toggle.start
       */
      toggleStart: (hasSelection: boolean, hasDocState: boolean, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.bold.toggle.start', 'onToggleBold - selection & docState', source, { hasSelection, hasDocState, ...data });
      },

      /**
       * Log paragraph ranges result
       * Component: orchestrator.formatting.bold.paragraphRanges
       */
      paragraphRanges: (rangeCount: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.bold.paragraphRanges', 'selectionToParagraphRanges result', source, { rangeCount, ...data });
      },

      /**
       * Log no paragraph ranges warning
       * Component: orchestrator.formatting.bold.gap.noRanges
       */
      noRanges: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('warn', 'orchestrator.formatting.bold.gap.noRanges', 'No paragraph ranges for selection - check paragraph bounds in docState above', source, data);
      },

      /**
       * Log toggling via WASM
       * Component: orchestrator.formatting.bold.wasm
       */
      wasm: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.bold.wasm', 'Toggling bold via WASM applyCharacterFormatting', source, data);
      },

      /**
       * Log paragraph font info
       * Component: orchestrator.formatting.bold.paragraphFont
       */
      paragraphFont: (paragraphFontFamily: string, paragraphFontWeight: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.bold.paragraphFont', 'Paragraph font info', source, { paragraphFontFamily, paragraphFontWeight, ...data });
      },

      /**
       * Log resolved bold font UID
       * Component: orchestrator.formatting.bold.resolvedFont
       */
      resolvedFont: (boldFontUid: string, family: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.bold.resolvedFont', `Resolved bold font_uid: ${boldFontUid} for family: ${family}`, source, { boldFontUid, family, ...data });
      },

      /**
       * Log applying via WASM
       * Component: orchestrator.formatting.bold.apply
       */
      apply: (newWeight: number, fontUid: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.bold.apply', 'Applying bold formatting via WASM', source, { newWeight, fontUid, ...data });
      },

      /**
       * Log bold complete
       * Component: orchestrator.formatting.bold.complete
       */
      complete: (wasApplied: boolean, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.bold.complete', `Bold toggle ${wasApplied ? 'applied' : 'removed'}`, source, { wasApplied, ...data });
      },
    },

    /**
     * Italic formatting operations
     */
    italic: {
      /**
       * Log no selection warning
       * Component: orchestrator.formatting.italic.gap.noSelection
       */
      noSelection: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('warn', 'orchestrator.formatting.italic.gap.noSelection', 'Cannot toggle italic: no selection or document state', source, data);
      },

      /**
       * Log italic toggle start
       * Component: orchestrator.formatting.italic.toggle.start
       */
      toggleStart: (hasSelection: boolean, hasDocState: boolean, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.italic.toggle.start', 'onToggleItalic - selection & docState', source, { hasSelection, hasDocState, ...data });
      },

      /**
       * Log paragraph ranges result
       * Component: orchestrator.formatting.italic.paragraphRanges
       */
      paragraphRanges: (rangeCount: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.italic.paragraphRanges', 'selectionToParagraphRanges result', source, { rangeCount, ...data });
      },

      /**
       * Log no paragraph ranges warning
       * Component: orchestrator.formatting.italic.gap.noRanges
       */
      noRanges: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('warn', 'orchestrator.formatting.italic.gap.noRanges', 'No paragraph ranges for italic selection', source, data);
      },

      /**
       * Log toggling via WASM
       * Component: orchestrator.formatting.italic.wasm
       */
      wasm: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.italic.wasm', 'Toggling italic via WASM applyCharacterFormatting', source, data);
      },

      /**
       * Log applying via WASM
       * Component: orchestrator.formatting.italic.apply
       */
      apply: (newStyle: string, fontUid: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.italic.apply', 'Applying italic formatting via WASM', source, { newStyle, fontUid, ...data });
      },

      /**
       * Log italic complete
       * Component: orchestrator.formatting.italic.complete
       */
      complete: (wasApplied: boolean, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.italic.complete', `Italic toggle ${wasApplied ? 'applied' : 'removed'}`, source, { wasApplied, ...data });
      },

      /**
       * Log loading italic font
       * Component: orchestrator.formatting.italic.loadFont.start
       */
      loadFontStart: (fontUid: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.italic.loadFont.start', `Loading italic font: ${fontUid}`, source, { fontUid, ...data });
      },

      /**
       * Log italic font loaded
       * Component: orchestrator.formatting.italic.loadFont.complete
       */
      loadFontComplete: (fontUid: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.italic.loadFont.complete', `Italic font loaded: ${fontUid}`, source, { fontUid, ...data });
      },

      /**
       * Log italic font load failed
       * Component: orchestrator.formatting.italic.loadFont.fail
       */
      loadFontFail: (fontUid: string, error?: unknown, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('error', 'orchestrator.formatting.italic.loadFont.fail', `Failed to load italic font: ${fontUid}`, source, { fontUid, error: error ? String(error) : undefined, ...data });
      },

      /**
       * Log setting pending italic format
       * Component: orchestrator.formatting.italic.pending
       */
      pending: (fontUid: string, fontStyle: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.italic.pending', 'Setting pending italic format (collapsed cursor)', source, { fontUid, fontStyle, ...data });
      },
    },

    /**
     * Underline formatting operations
     */
    underline: {
      /**
       * Log no selection warning
       * Component: orchestrator.formatting.underline.gap.noSelection
       */
      noSelection: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('warn', 'orchestrator.formatting.underline.gap.noSelection', 'Cannot toggle underline: no selection or document state', source, data);
      },

      /**
       * Log underline toggle
       * Component: orchestrator.formatting.underline.toggle
       */
      toggle: (newValue: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.underline.toggle', `Toggling underline to: ${newValue}`, source, { newValue, ...data });
      },
    },

    /**
     * Clear formatting operations
     */
    clear: {
      /**
       * Log clear formatting start
       * Component: orchestrator.formatting.clear.start
       */
      start: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.clear.start', 'Clearing formatting from selection', source, data);
      },

      /**
       * Log clear formatting complete
       * Component: orchestrator.formatting.clear.complete
       */
      complete: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'orchestrator.formatting.clear.complete', 'Formatting cleared', source, data);
      },
    },
  },

  // ============================================================================
  // EDITOR STATUS - orchestrator.editor.*
  // ============================================================================
  editor: {
    /**
     * Log editor status
     * Component: orchestrator.editor.status
     */
    status: (kind: string, message: unknown, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const level = kind === 'error' ? 'error' : kind === 'warn' ? 'warn' : 'debug';
      WeBelieveLogger.logWithSource(level, `orchestrator.editor.status.${kind}`, `EditorStatus: ${JSON.stringify(message)}`, source, { kind, message, ...data });
    },
  },

  // ============================================================================
  // ERROR HANDLING - orchestrator.error.*
  // ============================================================================
  error: {
    /**
     * Log bootstrap failed
     * Component: orchestrator.error.bootstrap
     */
    bootstrap: (error: unknown, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'orchestrator.error.bootstrap', 'bootstrap failed', source, { error: String(error), ...data });
    },

    /**
     * Log meta fetch failed (non-fatal)
     * Component: orchestrator.error.metaFetch
     */
    metaFetch: (error: unknown, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'orchestrator.error.metaFetch', 'Background meta fetch failed (non-fatal)', source, { error: String(error), ...data });
    },

    /**
     * Log font warmup failed
     * Component: orchestrator.error.fontWarmup
     */
    fontWarmup: (error: unknown, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'orchestrator.error.fontWarmup', 'Font warmup failed', source, { error: String(error), ...data });
    },

    /**
     * Log preferences load failed
     * Component: orchestrator.error.preferencesLoad
     */
    preferencesLoad: (error: unknown, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'orchestrator.error.preferencesLoad', 'Failed to load system preferences', source, { error: String(error), ...data });
    },

    /**
     * Log font read failed
     * Component: orchestrator.error.fontRead
     */
    fontRead: (error: unknown, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'orchestrator.error.fontRead', 'Failed to read font preference, using fallback', source, { error: String(error), ...data });
    },

    /**
     * Log font size read failed
     * Component: orchestrator.error.fontSizeRead
     */
    fontSizeRead: (error: unknown, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'orchestrator.error.fontSizeRead', 'Failed to read font size preference, using fallback', source, { error: String(error), ...data });
    },

    /**
     * Log preferences fallback failed
     * Component: orchestrator.error.preferencesFallback
     */
    preferencesFallback: (error: unknown, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'orchestrator.error.preferencesFallback', 'Failed to load system preferences for font fallback, using default', source, { error: String(error), ...data });
    },
  },

  // ============================================================================
  // LEGACY COMPATIBILITY - Maps old method signatures to v2 taxonomy
  // These maintain backward compatibility while emitting v2 component names
  // ============================================================================

  // Legacy init nested object
  legacyInit: {
    /** @deprecated Use OrchestratorLogger.init.start() */
    services: (docId: string, versionId: string | null, preferredEditor: string, data?: Record<string, unknown>) => {
      OrchestratorLogger.init.start(docId, versionId, preferredEditor, data);
    },
    /** @deprecated Use OrchestratorLogger.init.service.created() */
    documentServiceCreated: (instanceId: string, hasGetCachedDocument: boolean, hasGetDocumentMeta: boolean, data?: Record<string, unknown>) => {
      OrchestratorLogger.init.service.created(instanceId, hasGetCachedDocument, hasGetDocumentMeta, data);
    },
    /** @deprecated Use OrchestratorLogger.init.meta.trigger() */
    metaEffectTriggered: (docId: string, hasServices: boolean, hasDocument: boolean, data?: Record<string, unknown>) => {
      OrchestratorLogger.init.meta.trigger(docId, hasServices, hasDocument, data);
    },
    /** @deprecated Use OrchestratorLogger.init.title.fromUrl() */
    usingInitialTitle: (initialTitle: string, data?: Record<string, unknown>) => {
      OrchestratorLogger.init.title.fromUrl(initialTitle, data);
    },
    /** @deprecated Use OrchestratorLogger.init.cache.check() */
    cacheCheck: (docId: string, hasCached: boolean, cachedTitle?: string, data?: Record<string, unknown>) => {
      OrchestratorLogger.init.cache.check(docId, hasCached, cachedTitle, data);
    },
    /** @deprecated Use OrchestratorLogger.init.meta.result() */
    metaResult: (meta: Record<string, unknown> | null, hasTitle: boolean, data?: Record<string, unknown>) => {
      OrchestratorLogger.init.meta.result(meta, hasTitle, data);
    },
    /** @deprecated Use OrchestratorLogger.init.meta.complete() */
    backgroundMetaComplete: (backendTitle: string | undefined, preservedTitle: string, usingInitialTitle: boolean, data?: Record<string, unknown>) => {
      OrchestratorLogger.init.meta.complete(backendTitle, preservedTitle, usingInitialTitle, data);
    },
  },

  // Legacy render nested object
  legacyRender: {
    /** @deprecated Use OrchestratorLogger.render.check() */
    check: (data?: Record<string, unknown>) => {
      OrchestratorLogger.render.check(data);
    },
    /** @deprecated Use OrchestratorLogger.render.start() */
    rendering: (editorType: string, data?: Record<string, unknown>) => {
      OrchestratorLogger.render.start(editorType, data);
    },
    /** @deprecated Use OrchestratorLogger.render.editorSelect() */
    editorSelection: (data?: Record<string, unknown>) => {
      OrchestratorLogger.render.editorSelect(data);
    },
  },

  // Legacy font nested object
  legacyFont: {
    /** @deprecated Use OrchestratorLogger.font.warmup.start() */
    warmupStart: (data?: Record<string, unknown>) => {
      OrchestratorLogger.font.warmup.start(data);
    },
    /** @deprecated Use OrchestratorLogger.font.warmup.progress() */
    warmupProgress: (progress: number, phase: string, data?: Record<string, unknown>) => {
      OrchestratorLogger.font.warmup.progress(progress, phase, data);
    },
    /** @deprecated Use OrchestratorLogger.font.warmup.complete() */
    warmupComplete: (durationMs: number, data?: Record<string, unknown>) => {
      OrchestratorLogger.font.warmup.complete(durationMs, data);
    },
    /** @deprecated Use OrchestratorLogger.font.preferences.loadStart() */
    loadingSystemPreferences: (data?: Record<string, unknown>) => {
      OrchestratorLogger.font.preferences.loadStart(data);
    },
    /** @deprecated Use OrchestratorLogger.font.preferences.loadComplete() */
    systemPreferencesLoaded: (preferences: Record<string, unknown>, data?: Record<string, unknown>) => {
      OrchestratorLogger.font.preferences.loadComplete(preferences, data);
    },
    /** @deprecated Use OrchestratorLogger.font.preferences.resolved() */
    usingSystemPreferenceFont: (prefFont: string, mappedFont: string, data?: Record<string, unknown>) => {
      OrchestratorLogger.font.preferences.resolved(prefFont, mappedFont, data);
    },
    /** @deprecated Use OrchestratorLogger.font.preferences.unknown() */
    unknownSystemPreferenceFont: (prefFont: string, data?: Record<string, unknown>) => {
      OrchestratorLogger.font.preferences.unknown(prefFont, data);
    },
    /** @deprecated Use OrchestratorLogger.font.fallback.using() */
    usingFallbackFont: (fontName: string, data?: Record<string, unknown>) => {
      OrchestratorLogger.font.fallback.using(fontName, data);
    },
    /** @deprecated Use OrchestratorLogger.font.restore.complete() */
    restoredFromDocument: (fontFamily: string, fontWeight?: number, fontStyle?: string, data?: Record<string, unknown>) => {
      OrchestratorLogger.font.restore.complete(fontFamily, fontWeight, fontStyle, data);
    },
    /** @deprecated Use OrchestratorLogger.font.restore.skip() */
    restorationSkipped: (reason: string, data?: Record<string, unknown>) => {
      OrchestratorLogger.font.restore.skip(reason, data);
    },
    /** @deprecated Use OrchestratorLogger.font.update() */
    fontFamilyUpdated: (fontFamily: string, data?: Record<string, unknown>) => {
      OrchestratorLogger.font.update(fontFamily, data);
    },
    /** @deprecated Use OrchestratorLogger.font.fallback.set() */
    fallbackSet: (fontUid: string, documentFont: string, data?: Record<string, unknown>) => {
      OrchestratorLogger.font.fallback.set(fontUid, documentFont, data);
    },
    /** @deprecated Use OrchestratorLogger.font.defaultSize() */
    defaultFontSizeInit: (fontSize: number, data?: Record<string, unknown>) => {
      OrchestratorLogger.font.defaultSize(fontSize, data);
    },
  },

  // Legacy pageSetup nested object
  legacyPageSetup: {
    /** @deprecated Use OrchestratorLogger.pageSetup.init() */
    initFromMeta: (pageSetup: Record<string, unknown>, data?: Record<string, unknown>) => {
      OrchestratorLogger.pageSetup.init(pageSetup, data);
    },
  },

  // Legacy save nested object
  legacySave: {
    /** @deprecated Use OrchestratorLogger.save.start() */
    started: (docId: string, versionId: string | null, data?: Record<string, unknown>) => {
      OrchestratorLogger.save.start(docId, versionId, data);
    },
    /** @deprecated Use OrchestratorLogger.save.state.captured() */
    stateCaptured: (textLength: number, textPreview: string, hasParagraphs: boolean, paragraphCount?: number, data?: Record<string, unknown>) => {
      OrchestratorLogger.save.state.captured(textLength, textPreview, hasParagraphs, paragraphCount, data);
    },
    /** @deprecated Use OrchestratorLogger.save.state.full() */
    capturedState: (textLength: number, paragraphCount: number, hasPageSetup: boolean, hasFontSettings: boolean, data?: Record<string, unknown>) => {
      OrchestratorLogger.save.state.full(textLength, paragraphCount, hasPageSetup, hasFontSettings, data);
    },
    /** @deprecated Use OrchestratorLogger.save.ast() */
    astPayload: (blockCount: number, data?: Record<string, unknown>) => {
      OrchestratorLogger.save.ast(blockCount, data);
    },
    /** @deprecated Use OrchestratorLogger.save.complete() */
    complete: (docId: string, durationMs: number, data?: Record<string, unknown>) => {
      OrchestratorLogger.save.complete(docId, durationMs, data);
    },
    /** @deprecated Use OrchestratorLogger.save.fail() */
    failed: (docId: string, error: string, data?: Record<string, unknown>) => {
      OrchestratorLogger.save.fail(docId, error, data);
    },
  },

  // Legacy fontSettings nested object
  fontSettings: {
    /** @deprecated Use OrchestratorLogger.font.restore operations */
    restoring: (fontFamily?: string, fontWeight?: number, fontStyle?: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'orchestrator.font.restore.start', 'Restoring font settings from document', source, {
        fontFamily,
        fontWeight,
        fontStyle,
        ...data
      });
    },
  },
};

export default OrchestratorLogger;
